// Step 1: Select supplier and enter note for a stock import.
// Supplier field is a searchable combobox: type to filter existing suppliers,
// or create a new one inline when none match (no need to leave the import form).
import { type Dispatch, type SetStateAction, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Supplier {
  id: string;
  name: string;
  phone: string;
}

interface Warehouse {
  id: string;
  name: string;
  isActive: boolean;
  isDefault: boolean;
}

export interface Step1Data {
  supplierId: string;
  warehouseId: string;
  note: string;
}

interface Props {
  data: Step1Data;
  onChange: Dispatch<SetStateAction<Step1Data>>;
}

export function ImportFormStepOne({ data, onChange }: Props) {
  const qc = useQueryClient();

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () =>
      apiClient
        .get<{ data: Supplier[] }>("/v1/suppliers")
        .then((r) => r.data.data),
  });

  const { data: warehouses = [] } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () =>
      apiClient
        .get<{ data: Warehouse[] }>("/v1/warehouses")
        .then((r) => r.data.data),
  });

  const activeWarehouses = warehouses.filter((w) => w.isActive);

  // Supplier combobox state
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const selected = suppliers.find((s) => s.id === data.supplierId) ?? null;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? suppliers.filter(
        (s) =>
          s.name.toLowerCase().includes(q) || (s.phone ?? "").includes(q),
      )
    : suppliers;
  const hasExactName = suppliers.some(
    (s) => s.name.trim().toLowerCase() === q,
  );

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      apiClient
        .post<{ data: Supplier }>("/v1/suppliers", { name })
        .then((r) => r.data.data),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      onChange((d) => ({ ...d, supplierId: created.id }));
      setSearch("");
      setOpen(false);
    },
  });

  function selectSupplier(s: Supplier) {
    onChange((d) => ({ ...d, supplierId: s.id }));
    setSearch("");
    setOpen(false);
  }

  function clearSupplier() {
    onChange((d) => ({ ...d, supplierId: "" }));
    setSearch("");
  }

  return (
    <div className="space-y-4 max-w-md">
      <div className="space-y-1.5">
        <Label htmlFor="warehouse">Kho nhập <span className="text-destructive">*</span></Label>
        <select
          id="warehouse"
          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={data.warehouseId}
          onChange={(e) => onChange((d) => ({ ...d, warehouseId: e.target.value }))}
        >
          <option value="">— Chọn kho —</option>
          {activeWarehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}{w.isDefault ? " (mặc định)" : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="supplier">Nhà cung cấp</Label>

        {selected ? (
          // Chosen supplier — show as a chip with a clear button.
          <div className="flex h-9 items-center justify-between gap-2 rounded-md border border-input bg-muted/40 px-3 text-sm">
            <span className="truncate font-medium">
              {selected.name}
              {selected.phone ? (
                <span className="ml-2 text-muted-foreground">{selected.phone}</span>
              ) : null}
            </span>
            <button
              type="button"
              onClick={clearSupplier}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Bỏ chọn nhà cung cấp"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="relative">
            <Input
              id="supplier"
              placeholder="Gõ tên hoặc SĐT nhà cung cấp…"
              value={search}
              autoComplete="off"
              onChange={(e) => {
                setSearch(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
            />

            {open && (
              <ul className="absolute z-10 mt-1 w-full rounded-md border bg-background shadow-md text-sm max-h-56 overflow-auto">
                {filtered.map((s) => (
                  <li
                    key={s.id}
                    className="px-3 py-2 cursor-pointer hover:bg-accent"
                    // onMouseDown fires before input blur, so the click registers.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectSupplier(s);
                    }}
                  >
                    <span className="font-medium">{s.name}</span>
                    {s.phone ? (
                      <span className="ml-2 text-muted-foreground">{s.phone}</span>
                    ) : null}
                  </li>
                ))}

                {q !== "" && !hasExactName && (
                  <li
                    className="px-3 py-2 cursor-pointer hover:bg-accent border-t text-primary"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (!createMutation.isPending) createMutation.mutate(search.trim());
                    }}
                  >
                    {createMutation.isPending
                      ? "Đang tạo…"
                      : <>+ Tạo nhà cung cấp mới: <span className="font-medium">{search.trim()}</span></>}
                  </li>
                )}

                {filtered.length === 0 && q === "" && (
                  <li className="px-3 py-2 text-muted-foreground">Chưa có nhà cung cấp — gõ để tạo mới</li>
                )}
              </ul>
            )}
          </div>
        )}

        {createMutation.isError && (
          <p className="text-xs text-destructive">
            Không tạo được nhà cung cấp. Vui lòng thử lại.
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Bỏ trống nếu chưa có nhà cung cấp.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="note">Ghi chú</Label>
        <Input
          id="note"
          placeholder="Ghi chú phiếu nhập…"
          value={data.note}
          onChange={(e) => onChange((d) => ({ ...d, note: e.target.value }))}
        />
      </div>
    </div>
  );
}
